import { differenceInWeeks } from 'date-fns';
import { Event, EventTypeEnum } from '../../../graph-queries/__generated__/graphql';
import { useSelectedAsset } from '../../../lib/util';
import { calculateKpiPercentage } from '../KpiCard/KpiCard';
import { KpiCardData, KpiMetricType, useKpiMetrics } from '../KpiSection/KpiSection.util';
import { tcpCurveTargets, ocpCurveTargets } from './ReadinessCheck.constants';
import { useQuery } from '@apollo/client';
import { GET_READINESS_METRICS_BY_EVENT } from '../../../graph-queries/tar/readinessMetric';
import _ from 'lodash';

const redinessMetricTypes: KpiMetricType[] = [
  'PERMITS_VERIFIED',
  'IDPS_AWAITING_AUTHORISATION',
  'PROCEDURES_APPROVED',
  'LINE_WALK_PACKS_APPROVED',
  'GOC_READY_TO_USE',
  'TEST_PACKS_APPROVED',
  'JOBS_AT_LEVEL_4_SCHEDULED',
  'WORKPACKS_APPROVED',
  'LIFT_PLANS_APPROVED',
  'SCAFFOLDS_SURVEYED_AND_PLANNED',
  'INSULATION_SURVEYED_AND_PLANNED',
  'ISOLATIONS_WAITING_FOR_AUTHORIZATION',
  // 'MOCS_APPROVED',
  // 'JOBS_FIELD_WALKED',
  // 'JOBS_AT_LEVEL_3_SCHEDULED',
];

export const useReadinessCheck = (event: Event) => {
  const asset = useSelectedAsset();
  const kpiMetrics = useKpiMetrics(asset, event);

  const readinessMetrics = redinessMetricTypes
    .map((metricType) => kpiMetrics[metricType])
    .filter((data) => data && !data.loading && data.totalCount > 0) as KpiCardData[];

  const readinessCheck = {
    readinessPerc: _.floor(
      readinessMetrics
        .map((d) => calculateKpiPercentage(d.completionCount, d.totalCount))
        .reduce((a, b) => a + b, 0) / readinessMetrics.length,
      2
    ),
    weekNumber: getWeekNumberFromTimestamp(Date.now(), event),
    loading: !!readinessMetrics.find((d) => d.loading),
    error: !readinessMetrics.length || !!readinessMetrics.find((d) => d.error),
  };

  return readinessCheck;
};

export const getTargetKpi = (event: Event, targetName: keyof typeof sCurveTargets) => {
  if (!event.eventType) return { target: 0, error: 'not_available' };

  const sCurveTargets = event.eventType === EventTypeEnum.Tar ? tcpCurveTargets : ocpCurveTargets;
  const weekNumber = differenceInWeeks(new Date(), event.eventStartDate);

  if (weekNumber < -52) return { target: 0 };
  if (weekNumber > 1) return { target: 100 };

  const target = sCurveTargets[targetName]?.[weekNumber + 52];
  if (target === undefined) return { target: 0, error: 'not_available' };

  return { target };
};

const getWeekNumberFromTimestamp = (timestamp: Date | number, event: Event) => {
  const weeksSinceStartDate = differenceInWeeks(timestamp, event.eventStartDate);

  return weeksSinceStartDate;
};

export const useReadinessHistory = (event: Event) => {
  const {
    data: readinessMetrics,
    loading,
    error,
  } = useQuery(GET_READINESS_METRICS_BY_EVENT, {
    variables: { eventId: event.eventId },
  });

  const history = readinessMetrics?.readinessMetricsByEventId.map((r) => ({
    readinessPerc: _.floor(r.readinessPerc, 2),
    weekNumber: getWeekNumberFromTimestamp(r.updatedAt, event),
  }));

  const data = _.transform(
    _.groupBy(history, 'weekNumber'),
    (acc, curr) => {
      const maxObj = _.maxBy(curr, 'readinessPerc');
      maxObj && acc.push(maxObj);
    },
    [] as typeof history
  );

  return { data, loading, error };
};
